import React, { useState } from 'react';
import moment from 'moment';
import toast from 'react-hot-toast';
import { fromPhotos, fromStorage } from '../../../utils';
import { DownloadIcon, PlaceholderCarIcon, PlaceholderUserIcon } from '../../../assets/icons';
import { UserService } from '../../../services';

const ViewDriver = ({ item, onClose, onDelete, onEdit }) => {
  const [loading, setLoading] = useState(false);
  const licensePhoto = item?.person?.driver?.license || item?.license;
  const medicalCertPhoto = item?.person?.driver?.medical_cert || item?.medical_cert;
  const driverPhoto = item?.person?.photo;
  const vehiclePhoto = item?.person?.driver?.vehicle?.photo;

  const downloadFile = (url) => {
    if (url) window.open(url, '_blank');
  };

  const onPrint = async () => {
    setLoading(true);
    try {
      const response = await UserService.admin.downloadDriverDetails(item?.id);
      if (response.url) {
        const link = document.createElement('a');
        link.href = fromStorage(response.url);
        link.target = '_blank';
        link.download = response.url.split('/').pop();
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      toast.error(String(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
      <div className="flex justify-between items-start p-6 pb-2">
        <div>
          <h2 className="text-2xl font-bold text-text">{ item?.person?.name }</h2>
          <p className="text-orange font-semibold text-lg uppercase">
            { item.person.document }
          </p>
        </div>
        <button onClick={onClose} className="text-default hover:text-text transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>

      <div className="p-6 pt-2 space-y-6">
        <div className="flex gap-12">
          <div className="flex items-center gap-3">
            <div className="relative">
              <img
                src={driverPhoto ? fromPhotos(driverPhoto) : PlaceholderUserIcon}
                alt="Conductor"
                className="w-16 h-16 rounded-full object-cover border-2 border-orange"
              />
            </div>
            <span className="text-text font-medium">Foto del conductor</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative">
              <img
                src={vehiclePhoto ? fromPhotos(vehiclePhoto) : PlaceholderCarIcon}
                alt="Vehículo"
                className="w-16 h-16 rounded-full object-cover border-2 border-orange"
              />
            </div>
            <span className="text-text font-medium">Foto del vehículo</span>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-default text-sm">Correo</p>
            <p className="text-text font-medium">{item?.email || '-'}</p>
          </div>
          <div>
            <p className="text-default text-sm">Teléfono</p>
            <p className="text-text font-medium">{item?.person?.phone || '-'}</p>
          </div>
        </div>

        <div>
          <p className="text-default text-sm">Dirección</p>
          <p className="text-text leading-tight">
            {item?.person?.address_house || '-'}
          </p>
        </div>

        <div className="border-t border-orange pt-4">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-default text-sm">Licencia</p>
              <p className="text-text font-medium">{item?.person?.driver?.license_type || '-'}</p>
            </div>
            <div>
              <p className="text-default text-sm">Fecha de vencimiento</p>
              <p className="text-text font-medium">
                {item?.person?.driver?.license_expiration
                  ? moment(item.person.driver.license_expiration).format('DD/MM/YYYY')
                  : '-'}
              </p>
            </div>
          </div>
          <p className="text-default text-sm mb-2">Foto de licencia</p>
          <div className="bg-gray5 rounded-xl p-3 flex items-center justify-between gap-2 max-w-xs">
            <img
              src={licensePhoto ? fromPhotos(licensePhoto) : PlaceholderUserIcon}
              alt="doc"
              className="w-10 h-10 rounded-md object-cover"
            />
            <p className="text-sm font-bold w-full truncate">
              Foto de licencia.jpg
            </p>
            {licensePhoto && (
              <button className="text-orange" onClick={() => downloadFile(fromPhotos(licensePhoto))}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="border-t border-orange pt-4">
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-default text-sm">Número certificado medico</p>
              <p className="text-text font-medium">{item?.person?.driver?.medical_cert_type || '-'}</p>
            </div>
            <div>
              <p className="text-default text-sm">Fecha de vencimiento</p>
              <p className="text-text font-medium">
                {item?.person?.driver?.medical_cert_expiration
                  ? moment(item.person.driver.medical_cert_expiration).format('DD/MM/YYYY')
                  : '-'}
              </p>
            </div>
          </div>
          <p className="text-default text-sm mb-2">Foto de Certificado medico</p>
          <div className="bg-gray5 rounded-xl p-3 flex items-center justify-between gap-2 max-w-xs">
            <img
              src={medicalCertPhoto ? fromPhotos(medicalCertPhoto) : PlaceholderUserIcon}
              alt="doc"
              className="w-10 h-10 rounded-md object-cover"
            />
            <p className="text-sm font-bold w-full truncate">
              Foto de certificado.jpg
            </p>
            {medicalCertPhoto && (
              <button className="text-orange" onClick={() => downloadFile(fromPhotos(medicalCertPhoto))}>
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="border-t border-orange pt-4 grid grid-cols-2 gap-y-4">
          <div>
            <p className="text-default text-sm">Placa</p>
            <p className="text-text font-medium">{item?.person?.driver?.vehicle?.plaque || '-'}</p>
          </div>
          <div>
            <p className="text-default text-sm">Marca</p>
            <p className="text-text font-medium">{item?.person?.driver?.vehicle?.brand || '-'}</p>
          </div>
          <div>
            <p className="text-default text-sm">Modelo</p>
            <p className="text-text font-medium">{item?.person?.driver?.vehicle?.model || '-'}</p>
          </div>
          <div>
            <p className="text-default text-sm">Tipo de vehículo</p>
            <p className="text-text font-medium">
              {item?.person?.driver?.vehicle?.vehicle_types_sep?.map(x => x?.vehicle_type?.name)?.join(', ') || '-'}
            </p>
          </div>
          <div>
            <p className="text-default text-sm">Año</p>
            <p className="text-text font-medium">{item?.person?.driver?.vehicle?.year || '-'}</p>
          </div>
          <div>
            <p className="text-default text-sm">Color</p>
            <p className="text-text font-medium">{item?.person?.driver?.vehicle?.color || '-'}</p>
          </div>
          <div>
            <p className="text-default text-sm">Condición</p>
            <p className="text-text font-medium">Aire acondicionado: {item?.person?.driver?.vehicle?.conditions?.air_conditioning ? 'Sí' : 'No'}</p>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex gap-4">
            <button className="font-bold text-[#396AFF]" onClick={onEdit}>
              Editar
            </button>
            <button className="font-bold text-red" onClick={onDelete}>
              Eliminar
            </button>
          </div>

          <button
            className="flex items-center bg-[#396AFF] text-white gap-1 px-3 py-1 rounded-full font-semibold disabled:opacity-60"
            onClick={onPrint}
            disabled={loading}
          >
            {loading ? (
              <svg className="animate-spin w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <img src={DownloadIcon} className="w-5 h-5 object-contain brightness-0 invert" alt="" />
            )}
            {loading ? 'Descargando...' : 'Imprimir'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewDriver;